'use client';

import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import type { Article, BlogPosting } from 'schema-dts';

import { JsonLd } from '@sola/ark-skippy/atoms/json-ld/json-ld.tsx';
import { Breadcrumb } from '@sola/ark-skippy/molecules/breadcrumb/breadcrumb.tsx';
import { ArticleTemplate } from '@sola/ark-skippy/templates/article/article-template.tsx';
import {
  buildArticleJsonLd,
  getArticleSchemaType,
} from '@sola/ark-skippy/utils/schema-utils.ts';
import { keys } from '@sola/contentful-model/keys.ts';
import { getArticlePath } from '@sola/contentful-model/lib/article.ts';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.ts';
import { LOCALES_CODES } from '@sola/contentful-model/lib/types.ts';
import { ArticleResponseDto } from '@sola/schema/lib/contentful.ts';
import { slashes } from '@sola/utils/url.ts';

import { extractArgsArticle } from '../logic.tsx';

export const ArticlePage = ({
  article,
  locale,
  experiments,
}: {
  article: ArticleResponseDto;
  locale: LOCALES_CODES;
  experiments?: string[];
}) => {
  const data = ArticleResponseDto.parse(article);
  const { t } = useTranslation(['skippy', 'articles']);
  const path = usePathname();

  const urlBase = data.taxonomyArticleType
    ? articlesUrlBase[data.taxonomyArticleType as keyof typeof articlesUrlBase]
    : '#article';

  const breadcrumbs = [
    {
      title: t(`skippy:home`, 'Home'),
      link: `/`,
    },
    ...(data.taxonomyArticleType === keys.articleTypes.pressRelease
      ? [
          {
            title: t(`skippy:pressCenter`, 'Press Center'),
            link: urlBase.split('/').slice(0, -1).join('/'),
          },
        ]
      : []),
    {
      title:
        t(
          `articles:taxonomy.articleTypes.${data.taxonomyArticleType}.pluralName`,
        ) || 'Article',
      link: urlBase,
    },
    {
      title: article.pageTitle || '',
      link: slashes(
        getArticlePath(article.slug, data.taxonomyArticleType),
        'both',
      ),
    },
  ];

  const props = extractArgsArticle(data);
  if (!props) {
    return null;
  }

  const schemaType = getArticleSchemaType(props.taxonomyArticleType);

  return (
    <>
      <Breadcrumb breadcrumbs={breadcrumbs} />
      <main id="main">
        <JsonLd<Article | BlogPosting>
          item={{
            '@context': 'https://schema.org',
            '@type': schemaType,
            ...buildArticleJsonLd(props, path),
          }}
        />
        <ArticleTemplate article={props} experiments={experiments} />
      </main>
    </>
  );
};
